import type { GameState } from '../state'
import { el } from './panels'

/** Keep this breakpoint aligned with the compact HUD rules in mobile.css. */
export const MOBILE_HUD_QUERY = '(max-width: 900px), (max-width: 1100px) and (max-height: 500px)'

/** Mobile controls share the regular build dock and minimap, so tutorials and
 * desktop hotkeys still lead to the same controls on a phone. */
export function initMobileUI(
  st: GameState,
  root: HTMLElement,
  topbar: HTMLElement,
  buildbar: HTMLElement,
  minimap: HTMLElement,
): void {
  const head = el('div', 'mobile-dock-head')
  head.appendChild(el('span', 'mobile-dock-title', 'Build'))
  const buildToggle = el('button', 'mobile-build-toggle', 'Hide buildings') as HTMLButtonElement
  buildToggle.type = 'button'
  buildToggle.setAttribute('aria-expanded', 'true')
  buildToggle.setAttribute('aria-controls', 'build-categories build-options')
  head.appendChild(buildToggle)
  buildbar.prepend(head)

  const setBuildCollapsed = (collapsed: boolean): void => {
    // Portrait has room for one bottom view: opening the build menu dismisses
    // the local info selection. The normal HUD watcher closes its sheet, and
    // selecting that entity again folds the dock back out of the way.
    if (!collapsed && isPortraitLayout() &&
      (st.selection.building !== null || st.selection.units.length > 0)) {
      st.selection = { building: null, units: [] }
      st.emit('ui:selection')
    }
    root.classList.toggle('mobile-build-collapsed', collapsed)
    buildToggle.textContent = collapsed ? 'Show buildings' : 'Hide buildings'
    buildToggle.setAttribute('aria-expanded', String(!collapsed))
    scheduleMeasure()
  }
  buildToggle.onclick = () => setBuildCollapsed(!root.classList.contains('mobile-build-collapsed'))
  st.on('ui:build', () => setBuildCollapsed(false))
  st.on('ui:build:reveal', () => setBuildCollapsed(false))

  minimap.id = 'realm-minimap'
  const mapToggle = el('button', 'mobile-map-toggle') as HTMLButtonElement
  mapToggle.type = 'button'
  const mapIcon = el('span', 'mobile-map-icon', '🗺')
  mapIcon.setAttribute('aria-hidden', 'true')
  mapToggle.append(mapIcon, el('span', 'mobile-map-label', 'Map'))
  mapToggle.setAttribute('aria-controls', minimap.id)
  const setMapOpen = (open: boolean): void => {
    root.classList.toggle('mobile-map-open', open)
    mapToggle.setAttribute('aria-expanded', String(open))
    mapToggle.setAttribute('aria-label', open ? 'Hide minimap' : 'Show minimap')
    mapToggle.title = open ? 'Hide minimap' : 'Show minimap'
  }
  setMapOpen(false)
  mapToggle.onclick = () => setMapOpen(!root.classList.contains('mobile-map-open'))
  root.appendChild(mapToggle)
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') setMapOpen(false)
  })

  const compact = window.matchMedia(MOBILE_HUD_QUERY)
  const landscapeDock = window.matchMedia('(min-width: 500px) and (max-width: 1100px) and (max-height: 500px)')
  const isPortraitLayout = (): boolean => compact.matches && !landscapeDock.matches
  const hasInfoSelection = (): boolean => !st.buildMode &&
    (st.selection.building !== null || st.selection.units.length > 0)
  let portraitLayout = isPortraitLayout()
  const handleLayoutResize = (): void => {
    const nextPortrait = isPortraitLayout()
    // Rotating back with an existing selection opens the info sheet again.
    // Ordinary resizes within this layout (including the keyboard) must not
    // override a player who explicitly chose Show buildings.
    if (nextPortrait && !portraitLayout && hasInfoSelection()) setBuildCollapsed(true)
    portraitLayout = nextPortrait
    scheduleMeasure()
  }
  let lastSelectionKey = '|'
  st.on('ui:selection', () => {
    const selection = st.selection
    const key = `${selection.building ?? ''}|${[...selection.units].sort((a, b) => a - b).join(',')}`
    if (key === lastSelectionKey) return
    lastSelectionKey = key
    // Make room for the newly opened info sheet, once per entity selection.
    // Tab changes, live updates and manually reopening the dock keep their
    // current layout; short landscape screens have a separate dock layout.
    if (isPortraitLayout() && hasInfoSelection()) {
      setBuildCollapsed(true)
    }
  })
  const chat = root.querySelector<HTMLInputElement>('.chat-input')
  const updateLayout = (): void => {
    if (chat) chat.placeholder = compact.matches ? 'Tap to chat…' : 'Press Enter to speak…'
    // A minimap left open in a larger layout should not cover the phone view
    // when the device is rotated or a desktop window is narrowed.
    setMapOpen(false)
    scheduleMeasure()
  }
  compact.addEventListener('change', updateLayout)

  // Measure actual rows after wrapping, safe-area insets and dock expansion.
  // Other overlays can then fit between the top bar and the build dock without
  // guessing heights in rem or depending on a particular phone's aspect ratio.
  let measureQueued = false
  const setPx = (name: string, value: number): void => {
    const px = `${Math.ceil(value)}px`
    if (root.style.getPropertyValue(name) !== px) root.style.setProperty(name, px)
  }
  function measure(): void {
    measureQueued = false
    const top = topbar.getBoundingClientRect()
    const dock = buildbar.getBoundingClientRect()
    setPx('--mobile-top-bottom', top.bottom)
    setPx('--mobile-build-height', dock.height)
    setPx('--mobile-dock-top', dock.height ? dock.top : window.innerHeight)
  }
  function scheduleMeasure(): void {
    if (measureQueued) return
    measureQueued = true
    requestAnimationFrame(measure)
  }
  const resizeObserver = new ResizeObserver(scheduleMeasure)
  resizeObserver.observe(topbar)
  resizeObserver.observe(buildbar)
  window.addEventListener('resize', handleLayoutResize)
  window.visualViewport?.addEventListener('resize', scheduleMeasure)
  window.visualViewport?.addEventListener('scroll', scheduleMeasure)
  updateLayout()
}
